import { FoldersSection } from "@/components/slides/toolbar/panels/medias/sections/FoldersSection";
import {
  GetMediaHouseAudioDocument,
  GetMediaHouseImagesDocument,
  SearchMediaHouseDocument,
} from "@/generated/graphql";
import { useCopyFromMediaHouse } from "@/lib/hooks/medias/useCopyFromMediaHouse";
import { Button, LoadingSpinner, Modal } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { HiOutlineSearch } from "react-icons/hi";

const PAGE_SIZE = 24;
const SEARCH_DELAY_MS = 300;

export interface PickedMedia {
  id: string;
  src: string;
  filename: string;
}

interface Asset {
  id: string;
  url: string;
  filename: string;
}

export function MediaHousePicker({
  isOpen,
  onClose,
  onPick,
  mediaType = "image",
}: {
  isOpen: boolean;
  onClose: () => void;
  onPick: (media: PickedMedia) => void;
  mediaType?: "image" | "audio";
}) {
  const isAudio = mediaType === "audio";
  const [folderId, setFolderId] = useState<string | null>(null);
  const [term, setTerm] = useState("");
  const [search, setSearch] = useState("");

  const { copyFromMediaHouse, copying } = useCopyFromMediaHouse();

  useEffect(() => {
    const timer = setTimeout(() => setSearch(term.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const browsed = useQuery(isAudio ? GetMediaHouseAudioDocument : GetMediaHouseImagesDocument, {
    variables: { folderId, first: PAGE_SIZE },
    skip: !isOpen || search !== "",
    fetchPolicy: "cache-and-network",
  });

  const found = useQuery(SearchMediaHouseDocument, {
    variables: { query: search, type: mediaType },
    skip: !isOpen || search === "",
    fetchPolicy: "cache-and-network",
  });

  const pageInfo = browsed.data?.mediaHouseMedia.pageInfo;
  const assets: Asset[] =
    search === ""
      ? (browsed.data?.mediaHouseMedia.edges ?? []).map((edge) => edge.node)
      : (found.data?.searchMediaHouse ?? []);
  const loading = search === "" ? browsed.loading : found.loading;

  const pick = async (mediaHouseId: string) => {
    const media = await copyFromMediaHouse(mediaHouseId);
    onPick(media);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isAudio ? "Pick a sound" : "Pick a picture"}
      size="xl"
    >
      <div className="space-y-3">
        <div className="relative">
          <HiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Search Media House…"
            className="w-full rounded-lg border border-gray-200 bg-background py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-800"
          />
        </div>

        {search === "" && (
          <FoldersSection parentId={folderId} mediaType={mediaType} onFolderClick={setFolderId} />
        )}

        <div className="max-h-[50vh] overflow-y-auto">
          {loading && assets.length === 0 ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : assets.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              {search === ""
                ? `This folder has no ${isAudio ? "sounds" : "pictures"}.`
                : `Nothing matches "${search}".`}
            </p>
          ) : isAudio ? (
            <ul className="space-y-1.5">
              {assets.map((asset) => (
                <li
                  key={asset.id}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-800"
                >
                  <span className="min-w-0 flex-1 truncate text-sm">{asset.filename}</span>
                  {/* biome-ignore lint/a11y/useMediaCaption: a sound effect has nothing to caption */}
                  <audio src={asset.url} controls preload="none" className="h-8 w-56 shrink-0" />
                  <Button size="sm" disabled={copying} onClick={() => void pick(asset.id)}>
                    Use
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {assets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  disabled={copying}
                  onClick={() => void pick(asset.id)}
                  title={asset.filename}
                  className="group aspect-square overflow-hidden rounded-lg border border-gray-200 transition-colors hover:border-primary-500 disabled:opacity-60 dark:border-gray-800"
                >
                  <img
                    src={asset.url}
                    alt={asset.filename}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {search === "" && pageInfo?.hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              isLoading={browsed.loading}
              onClick={() => void browsed.fetchMore({ variables: { after: pageInfo.endCursor } })}
            >
              Load more
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
